import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useUserStore } from '../../../stores/useUserStore';
import { useTeamStore } from '../../../stores/useTeamStore';
import { useSubscriptionLimits } from '../../../hooks/useSubscriptionLimits';
import { useAchievementsStore } from '../../../stores/useAchievementsStore';
import { useReferralNotifications } from '../../../hooks/useReferralNotifications';
import { useTelegramWebApp } from '../../../hooks/useTelegramWebApp';
import { useTheme } from '../../../hooks/useTheme';
import { hasAuthData, clearAuthData } from '../../../utils/authHeaders';
import styles from './DesktopHeader.module.css';
import clsx from 'clsx';

// Icons
import StarIcon from '../../../assets/icons/Star.svg';
import StarLogoBoxGreen from '../../../assets/Star_Logos/Green_Logo_Star_with_box.svg';
import StarLogoBoxWhite from '../../../assets/Star_Logos/White_Logo_Star_with_box.svg';
import StarLogoFallback from '../../../assets/Star_Logos/Black_Logo_Star_without_box.svg';
import { ChatIcon, ImageIcon, VideoIcon, ClipsIcon } from '../NavIcons';
import type { FC, SVGProps } from 'react';

interface NavItem {
  id: string;
  path: string;
  label: string;
  Icon: FC<SVGProps<SVGSVGElement>>;
}

// NAV_ITEMS moved inside component to use t()


const DesktopHeader = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUserStore();
  const { activeTeam } = useTeamStore();
  const { limits } = useSubscriptionLimits();
  const { hasUnreadAchievements } = useAchievementsStore();
  const { unviewedCount } = useReferralNotifications();
  const { haptic, user: telegramUser } = useTelegramWebApp();
  
  const { resolvedTheme, setTheme } = useTheme();

  const NAV_ITEMS: NavItem[] = useMemo(() => [
    { id: 'chat', path: '/chat', label: t('nav.chat'), Icon: ChatIcon },
    { id: 'image', path: '/image', label: t('nav.image'), Icon: ImageIcon },
    { id: 'video', path: '/video', label: t('nav.video'), Icon: VideoIcon },
    { id: 'clips', path: '/tools', label: t('nav.tools'), Icon: ClipsIcon },
  ], [t]);
  
  const navRef = useRef<HTMLElement>(null);
  // Состояние перетаскивания
  const [isDragging, setIsDragging] = useState(false);
  const dragTargetRef = useRef<HTMLElement | null>(null);

  const getTargetLeftAndWidth = useCallback((targetEl: HTMLElement) => {
    if (!navRef.current) return { left: 0, width: 0 };
    const navRect = navRef.current.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const targetLeft = targetRect.left - navRect.left;
    return { left: targetLeft, width: targetRect.width };
  }, []);

  // Handle Drag Move (follow mouse)
  const handleDragMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDragging || !navRef.current) return;
    
    // Получаем координаты из события
    let clientX = 0;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
    } else {
      clientX = e.clientX;
    }
    
        // Ищем элемент под мышью/пальцем
    const elements = document.elementsFromPoint(clientX, navRef.current.getBoundingClientRect().top + (navRef.current.clientHeight / 2));
    const itemEl = elements.find(el => 
      el.classList.contains(styles.navItem)
    ) as HTMLElement;
    
    if (itemEl) {
      if (itemEl.classList.contains(styles.disabled)) return;
      
      dragTargetRef.current = itemEl;
      const { left, width } = getTargetLeftAndWidth(itemEl);
      
      const indicator = navRef.current.querySelector(`.${styles.activeIndicator}`) as HTMLElement;
      if (indicator) {
        indicator.classList.add(styles.dragging);
        navRef.current.style.setProperty('--active-left', `${left}px`);
        navRef.current.style.setProperty('--active-width', `${width}px`);
      }
    }
  }, [isDragging, getTargetLeftAndWidth]);

  // Handle Drag End
  const handleDragEnd = useCallback(() => {
    if (!isDragging || !navRef.current) return;
    setIsDragging(false);
    
    const indicator = navRef.current.querySelector(`.${styles.activeIndicator}`) as HTMLElement;
    if (indicator) {
      indicator.classList.remove(styles.dragging);
    }
    
    if (dragTargetRef.current) {
      // Имитируем клик
      const clickEvent = new MouseEvent('click', { bubbles: true });
      dragTargetRef.current.dispatchEvent(clickEvent);
      // Возвращаем фокус на настоящий активный если мы просто отпустили
      setTimeout(() => {
        const activeEl = navRef.current?.querySelector(`.${styles.active}`) as HTMLElement;
        if (activeEl && navRef.current) {
           const { left, width } = getTargetLeftAndWidth(activeEl);
           navRef.current.style.setProperty('--active-left', `${left}px`);
           navRef.current.style.setProperty('--active-width', `${width}px`);
        }
      }, 50);
    }
  }, [isDragging, getTargetLeftAndWidth]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('touchmove', handleDragMove as any, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
      window.addEventListener('mousemove', handleDragMove as any);
      window.addEventListener('mouseup', handleDragEnd);
    }
    return () => {
      window.removeEventListener('touchmove', handleDragMove as any);
      window.removeEventListener('touchend', handleDragEnd);
      window.removeEventListener('mousemove', handleDragMove as any);
      window.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);
  
  // Проверяем авторизацию пользователя
  const isAuthenticated = hasAuthData() && !!user;
  
  // Проверяем наличие непрочитанных достижений
  const hasUnread = hasUnreadAchievements();
  const hasNewReferrals = unviewedCount > 0;
  
  // Team mode — единственный источник истины: Zustand store
  const { isTeamMode } = useTeamStore();
  // Проверка доступности видео
  const isVideoAvailable = limits?.video?.enabled !== false;
  
  // Определяем активную страницу
  const getActivePage = () => {
    const path = location.pathname;
    if (path.includes('/chat')) return 'chat';
    if (path.includes('/image')) return 'image';
    if (path.includes('/video')) return 'video';
    if (path.includes('/tools')) return 'clips';
    if (path === '/account') return 'account';
    return null;
  };
  const activePage = getActivePage();
  
  // Пути всегда чистые — team mode определяется стором, не URL
  const getPath = (basePath: string) => basePath;
  
  // Эффект для обновления позиции плавающего фона на десктопе
  useEffect(() => {
    // Небольшая задержка, чтобы DOM точно обновился
    const timer = setTimeout(() => {
      if (!navRef.current) return;
      
      // Ищем активный элемент (кнопку)
      const activeEl = navRef.current.querySelector(`.${styles.active}`) as HTMLElement;
      
      if (activeEl) {
        // Получаем позицию активного элемента
        const navRect = navRef.current.getBoundingClientRect();
        const activeRect = activeEl.getBoundingClientRect();
        const targetLeft = activeRect.left - navRect.left;
        
        const newLeft = `${targetLeft}px`;
        const indicator = navRef.current.querySelector(`.${styles.activeIndicator}`) as HTMLElement;
        
        if (indicator) {
          const currentLeft = indicator.style.getPropertyValue('--active-left');
          if (currentLeft && currentLeft !== newLeft) {
            indicator.classList.add(styles.moving);
            clearTimeout((indicator as any)._squishTimer);
            (indicator as any)._squishTimer = setTimeout(() => {
              indicator.classList.remove(styles.moving);
            }, 150);
          }
        }

        // Передаем значения в CSS переменные
        navRef.current.style.setProperty('--active-left', newLeft);
        navRef.current.style.setProperty('--active-width', `${activeRect.width}px`);
      } else {
        // Если нет активного, скрываем индикатор (или ставим 0)
        navRef.current.style.setProperty('--active-width', `0px`);
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [activePage, location.pathname]);

  const handleNavClick = (item: NavItem) => () => {
    if (isDragging) return;
    haptic.impact('light');
    
    // Если не авторизован - не переходим
    if (!isAuthenticated) return;
    
    // Блокировка видео для Free подписки
    if (item.id === 'video' && !isVideoAvailable && !isTeamMode) {
      navigate('/pricing');
      return;
    }
    
    navigate(getPath(item.path));
    window.dispatchEvent(new CustomEvent('navigation-change'));
  };
  
  const handleAccountClick = () => {
    haptic.impact('light');
    navigate('/account');
  };

  const handleLogout = () => {
    haptic.impact('medium');
    clearAuthData();
    window.location.href = '/';
  };

  const handleLogin = () => {
    haptic.impact('light');
    navigate('?auth=login');
  };
  
  // Форматирование токенов
  const formatTokens = (tokens: number) => {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M`;
    }
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    return tokens.toLocaleString();
  };
  
  // Получаем цвет бейджа подписки (как в ProfileScreen)
  const getSubscriptionBadgeColor = () => {
    const tier = user?.subscriptionTier;
    if (!tier || tier === 'free') return '#808080'; // Серый для free
    if (tier === 'start') return 'var(--color-start-500)'; // Жёлтый — token
    if (tier === 'basic') return '#FFFFFF'; // Белый для basic
    if (tier === 'pro') return '#1b95e0'; // Синий для pro
    if (tier === 'max') return 'linear-gradient(135deg, #7109AA 0%, #9b4dca 50%, #c77deb 100%)'; // Градиент для max
    return '#808080';
  };
  
  const subscriptionBadgeColor = getSubscriptionBadgeColor();
  
  // Фото пользователя из Telegram
  const userPhotoUrl = telegramUser?.photo_url || null;
  
  // Данные для отображения в зависимости от режима (личный/команда)
  const displayTokens = isTeamMode && activeTeam ? (activeTeam.tokens ?? 0) : (user?.tokens ?? 0);
  const [avatarError, setAvatarError] = useState(false);
  const displayAvatarUrl = isTeamMode && activeTeam ? activeTeam.avatarUrl : userPhotoUrl;
  const showAvatar = displayAvatarUrl && !avatarError;
  // В режиме команды используем фиолетовый градиент для аватарки
  const avatarBackground = isTeamMode 
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : subscriptionBadgeColor;
  
  return (
    <>
      <header className={styles.header}>
        <div className={styles.container}>
          {/* Left: Logo */}
          <div className={styles.left}>
            <Link to="/" className={styles.logo} onClick={() => haptic.impact('light')}>
              <img src={resolvedTheme === 'light' ? StarLogoBoxWhite : StarLogoBoxGreen} alt="Star AI" className={styles.logoIcon} />
              <span className={styles.logoText}>STAR AI</span>
            </Link>
          </div>
            
          {/* Center: Navigation */}
          <nav className={styles.nav} ref={navRef as React.RefObject<HTMLElement>}>
            {/* Индикатор текущего активного элемента */}
            <div className={styles.activeIndicator} style={{
              transform: `translate3d(var(--active-left, 0px), -50%, 0)`,
              width: `var(--active-width, 0px)`
            }} />
            
            {NAV_ITEMS.map((item) => {
              const isActive = activePage === item.id;
              const isDisabled = !isAuthenticated || (item.id === 'video' && !isVideoAvailable && !isTeamMode);
              
              return (
                  <button
                    key={item.id}
                    className={clsx(styles.navItem, {
                      [styles.active]: isActive && isAuthenticated,
                      [styles.disabled]: isDisabled,
                    })}
                    onClick={handleNavClick(item)}
                    onMouseDown={() => {
                      if (isAuthenticated && !isDisabled) setIsDragging(true);
                    }}
                    onTouchStart={() => {
                      if (isAuthenticated && !isDisabled) setIsDragging(true);
                    }}
                    disabled={isDisabled}
                  >
                  <item.Icon className={`${styles.navIcon} ${isActive && isAuthenticated ? styles.navIconActive : ''}`} />
                  <span className={styles.navLabel}>{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Right: Pricing + Tokens/SignIn + Account + Logout */}
          <div className={styles.right}>
            {/* Pricing Link - всегда видна */}
            <button 
              className={styles.pricingButton}
              onClick={() => {
                haptic.impact('light');
                navigate('/pricing');
              }}
            >
              {t('nav.pricing')}
            </button>
            
            {/* Theme Toggle */}
            <button
              className={styles.headerIconButton}
              onClick={() => {
                haptic.impact('light');
                setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
              }}
              aria-label={resolvedTheme === 'dark' ? t('nav.lightTheme') : t('nav.darkTheme')}
              title={resolvedTheme === 'dark' ? t('nav.lightTheme') : t('nav.darkTheme')}
            >
              {resolvedTheme === 'dark' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"/>
                  <line x1="12" y1="1" x2="12" y2="3"/>
                  <line x1="12" y1="21" x2="12" y2="23"/>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                  <line x1="1" y1="12" x2="3" y2="12"/>
                  <line x1="21" y1="12" x2="23" y2="12"/>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              )}
            </button>

            {isAuthenticated ? (
              <>
                {/* Tokens */}
                <button
                  className={styles.tokensButton}
                  onClick={() => {
                    haptic.impact('light');
                    navigate('/account?screen=usage');
                  }}
                  aria-label={t('nav.tokenBalance')}
                >
                  <img src={StarIcon} alt="" className={styles.tokenIcon} />
                  <span className={styles.tokenCount}>
                    {formatTokens(displayTokens)}
                  </span>
                  <span className={styles.tokenTooltip}>
                    {t('nav.tokenExact', { count: displayTokens })}
                  </span>
                </button>
                
                {/* Gallery */}
                <button
                  className={clsx(styles.headerIconButton, styles.hasTooltip)}
                  onClick={() => {
                    haptic.impact('light');
                    navigate('/gallery');
                  }}
                  aria-label={t('nav.gallery')}
                >
                  <svg width="20" height="20" viewBox="0 0 23 21" fill="none">
                    <path d="M19.5475 6.3916V14.5916C19.5475 16.2718 19.5475 17.1118 19.2205 17.7536C18.9329 18.3181 18.4739 18.777 17.9095 19.0646C17.2677 19.3916 16.4276 19.3916 14.7475 19.3916H7.34749C5.66733 19.3916 4.82725 19.3916 4.18551 19.0646C3.62103 18.777 3.16209 18.3181 2.87447 17.7536C2.54749 17.1118 2.54749 16.2718 2.54749 14.5916V6.3916M2.64749 1.3916H19.4475C20.0075 1.3916 20.2876 1.3916 20.5015 1.5006C20.6896 1.59647 20.8426 1.74945 20.9385 1.93761C21.0475 2.15152 21.0475 2.43155 21.0475 2.9916V4.7916C21.0475 5.35165 21.0475 5.63168 20.9385 5.84559C20.8426 6.03375 20.6896 6.18673 20.5015 6.28261C20.2876 6.3916 20.0075 6.3916 19.4475 6.3916H2.64749C2.08743 6.3916 1.80741 6.3916 1.59349 6.28261C1.40533 6.18673 1.25235 6.03375 1.15648 5.84559C1.04749 5.63168 1.04749 5.35165 1.04749 4.7916V2.9916C1.04749 2.43155 1.04749 2.15152 1.15648 1.93761C1.25235 1.74945 1.40533 1.59647 1.59349 1.5006C1.80741 1.3916 2.08743 1.3916 2.64749 1.3916ZM8.64749 9.8916H13.4475C14.0075 9.8916 14.2876 9.8916 14.5015 10.0006C14.6896 10.0965 14.8426 10.2494 14.9385 10.4376C15.0475 10.6515 15.0475 10.9315 15.0475 11.4916V12.2916C15.0475 12.8517 15.0475 13.1317 14.9385 13.3456C14.8426 13.5338 14.6896 13.6867 14.5015 13.7826C14.2876 13.8916 14.0075 13.8916 13.4475 13.8916H8.64749C8.08743 13.8916 7.80741 13.8916 7.59349 13.7826C7.40533 13.6867 7.25235 13.5338 7.15648 13.3456C7.04749 13.1317 7.04749 12.8517 7.04749 12.2916V11.4916C7.04749 10.9315 7.04749 10.6515 7.15648 10.4376C7.25235 10.2494 7.40533 10.0965 7.59349 10.0006C7.80741 9.8916 8.08743 9.8916 8.64749 9.8916Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className={styles.iconTooltip}>{t('nav.gallery')}</span>
                </button>

                <div className={styles.divider} />

                {/* Account */}
                <button
                  className={styles.accountButton}
                  onClick={handleAccountClick}
                  aria-label={t('nav.account')}
                >
                  <div
                    className={styles.avatarWrapper}
                    style={{
                      background: avatarBackground
                    }}
                  >
                    {showAvatar ? (
                      <img
                        src={displayAvatarUrl}
                        alt=""
                        className={styles.avatar}
                        onError={() => setAvatarError(true)}
                      />
                    ) : (
                      <div className={styles.avatarPlaceholder} style={{ background: 'linear-gradient(90deg, var(--button-color) 0%, var(--button-color-end) 100%)' }}>
                        <img src={StarLogoFallback} alt="" style={{ width: '60%', height: '60%', objectFit: 'contain' }} />
                      </div>
                    )}

                    {/* Notification badges - только в личном режиме */}
                    {!isTeamMode && (hasUnread || hasNewReferrals) && (
                      <span className={styles.notificationDot} />
                    )}
                  </div>
                  <span className={styles.iconTooltip}>{t('nav.account')}</span>
                </button>

                {/* Logout Button */}
                <button
                  className={styles.logoutButton}
                  onClick={handleLogout}
                  aria-label={t('nav.logout')}
                >
                  <i className="fa-solid fa-arrow-right-from-bracket" />
                  <span className={styles.iconTooltip}>{t('nav.logout')}</span>
                </button>
              </>
            ) : (
              /* Sign In Button - только для неавторизованных */
              <button className={styles.signInButton} onClick={handleLogin}>
                {t('nav.signIn')}
              </button>
            )}
          </div>
        </div>
      </header>
      
    </>
  );
};

export default DesktopHeader;
